import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, ChevronDown, Check, Locate, Loader2, Plus, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useDeliveryAddress } from "@/contexts/AddressContext";
import { nearestZone } from "@/utils/conakryZones";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * Sélecteur d'adresse de livraison (header page d'accueil, style Uber Eats).
 * Ligne 1 : "Bonjour {prénom}" si connecté, sinon "Livrer à".
 * Ligne 2 : adresse/zone sélectionnée (défaut : Conakry).
 */
export default function AddressSelector({ variant = "mobile" }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addresses, selected, select } = useDeliveryAddress();
  const [detecting, setDetecting] = useState(false);

  const firstName = user?.name ? user.name.trim().split(/\s+/)[0] : null;
  const currentLabel = selected ? (selected.label || selected.zone) : "Conakry";
  const itemCls = "flex items-center gap-2 px-3 py-2 rounded-xl text-sm cursor-pointer";

  const detectPosition = () => {
    if (detecting) return;
    if (!("geolocation" in navigator)) { toast.error(t("home.detectUnsupported")); return; }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDetecting(false);
        const match = nearestZone(pos.coords.latitude, pos.coords.longitude);
        if (match) {
          select({ label: match.zone, zone: match.zone });
          toast.success(t("home.detectSuccess", { zone: match.zone }));
        } else {
          toast.info(t("home.detectFallback"));
          select(null);
        }
      },
      (err) => {
        setDetecting(false);
        if (err.code === err.PERMISSION_DENIED) toast.error(t("home.detectDenied"));
        else toast.error(t("home.detectError"));
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`address-selector-trigger-${variant}`}
          className="flex items-center gap-2 rounded-full bg-elevated hover:bg-[#EBE8E0] pl-2.5 pr-3 py-1.5 min-w-0 max-w-[220px] sm:max-w-[280px] transition-colors"
        >
          <MapPin className="w-4 h-4 text-brand-red shrink-0" />
          <span className="min-w-0 text-left leading-tight">
            <span className="block text-[10px] text-mute truncate" data-testid={`address-selector-greeting-${variant}`}>
              {firstName ? t("addr.hello", { name: firstName }) : t("addr.deliverTo")}
            </span>
            <span className="block text-xs font-bold text-ink truncate" data-testid={`address-selector-label-${variant}`}>
              {currentLabel}
            </span>
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-mute shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 rounded-2xl p-2" data-testid={`address-selector-menu-${variant}`}>
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-mute flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" /> {t("addr.deliverTo")}
        </div>
        <DropdownMenuItem
          data-testid="address-opt-all"
          onSelect={() => select(null)}
          className={`${itemCls} ${!selected ? "font-semibold bg-elevated" : ""}`}
        >
          <MapPin className="w-4 h-4 text-brand-red" />
          <span className="flex-1 min-w-0 truncate">{t("addr.allConakry")}</span>
          {!selected && <Check className="w-3.5 h-3.5 shrink-0" />}
        </DropdownMenuItem>
        {addresses.map((a) => (
          <DropdownMenuItem
            key={a.address_id}
            data-testid={`address-opt-${a.address_id}`}
            onSelect={() => select({ label: a.label, zone: a.zone, address_id: a.address_id })}
            className={`${itemCls} ${selected?.address_id === a.address_id ? "font-semibold bg-elevated" : ""}`}
          >
            <MapPin className="w-4 h-4 text-mute shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block truncate">{a.label}</span>
              <span className="block text-[11px] text-mute truncate">{a.zone} · {a.address}</span>
            </span>
            {selected?.address_id === a.address_id && <Check className="w-3.5 h-3.5 shrink-0" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="address-opt-detect" onSelect={detectPosition} className={itemCls}>
          {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Locate className="w-4 h-4 text-brand-green" />}
          {t("addr.detect")}
        </DropdownMenuItem>
        {user ? (
          <DropdownMenuItem asChild data-testid="address-opt-manage" className={itemCls}>
            <Link to="/profile"><Plus className="w-4 h-4" /> {t("addr.manage")}</Link>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem asChild data-testid="address-opt-login" className={itemCls}>
            <Link to="/login"><LogIn className="w-4 h-4" /> {t("addr.loginToSave")}</Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
